import {useCallback, useEffect, useState} from "react";
import {useOutletContext} from "react-router";
import {Paper, useTheme, Box, Typography} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {apiGet} from "../utils";
import {buildV2QueryParams, hasDateFilters} from "../utils/filters";
import {ProfitDriversSummary} from "../components/ProfitDriversSummary";
import {ProfitDriversWaterfall} from "../components/ProfitDriversWaterfall";
import {MeasureDetailTable} from "../components/MeasureDetailTable";
import {InsightsPanel} from "../utils/panels";
import {LoadingPanel} from "../utils/loading_spinner";

export const ProfitDriversContainer = () => {
    const {filters} = useOutletContext();
    const theme = useTheme();

    const [pvmData, setPvmData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedImpact, setSelectedImpact] = useState(null);
    const [detailData, setDetailData] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    const fetchPvmData = useCallback(() => {
        if (!hasDateFilters(filters)) {
            return;
        }

        setLoading(true);
        const queryParams = buildV2QueryParams(filters);

        apiGet(`v2/profit-drivers/analysis${queryParams}`, (resp) => {
            if (resp.success) {
                setPvmData(resp.data);
            } else {
                console.error('PVM analysis API error:', resp);
                setPvmData(null);
            }
            setLoading(false);
        });
    }, [filters]);

    const fetchImpactDetail = useCallback(() => {
        if (!selectedImpact || !hasDateFilters(filters)) {
            setDetailData(null);
            return;
        }

        setDetailLoading(true);
        const queryParams = buildV2QueryParams(filters);
        const separator = queryParams.includes('?') ? '&' : '?';

        apiGet(
            `v2/profit-drivers/detail${queryParams}${separator}impact_type=${selectedImpact.key}`,
            (resp) => {
                if (resp.success) {
                    setDetailData(resp.data);
                } else {
                    console.error('PVM detail API error:', resp);
                    setDetailData(null);
                }
                setDetailLoading(false);
            }
        );
    }, [filters, selectedImpact]);

    useEffect(() => {
        fetchPvmData();
    }, [fetchPvmData]);

    useEffect(() => {
        setSelectedImpact(null);
        setDetailData(null);
    }, [filters]);

    useEffect(() => {
        fetchImpactDetail();
    }, [fetchImpactDetail]);

    const handleImpactSelect = useCallback((row) => {
        setSelectedImpact((current) => (
            current?.key === row.key ? null : row
        ));
    }, []);

    return (
        <div style={{marginTop: 30, marginBottom: 100, marginLeft: 30, marginRight: 30}}>
            {loading ? (
                <LoadingPanel loading={true} message="Loading profit driver analysis..." />
            ) : (
                <>
                    <Grid container columnSpacing={2}>
                        <Grid size={4}>
                            <ProfitDriversSummary
                                data={pvmData}
                                selectedImpactKey={selectedImpact?.key}
                                onImpactSelect={handleImpactSelect}
                            />
                        </Grid>

                        <Grid size={8}>
                            <Box sx={{mb: 2}}>
                                <ProfitDriversWaterfall data={pvmData?.waterfall} />
                            </Box>

                            <Paper sx={{...theme.palette.contentPanel, padding: 2, borderRadius: 2}}>
                                <Grid container spacing={2}>
                                    <Grid size={6}>
                                        <InsightsPanel
                                            content="Pricing actions increased margins by $2M YTD"
                                        />
                                    </Grid>

                                    <Grid size={6}>
                                        <InsightsPanel
                                            content="Drivers are segment A and product category B"
                                            is_insight={false}
                                        />
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Grid>
                    </Grid>

                    {selectedImpact && (
                        <Paper sx={{...theme.palette.contentPanel, padding: 2, borderRadius: 2, mt: 2}}>
                            <MeasureDetailTable
                                breakdown={detailData?.breakdown}
                                calcConfigs={detailData?.calcConfigs}
                                groupBy={detailData?.groupBy || 'account_id'}
                                loading={detailLoading}
                                title={`Profit Drivers Selected: ${selectedImpact.label}`}
                            />
                        </Paper>
                    )}

                    {pvmData && (
                        <Box sx={{mt: 2}}>
                            <Typography variant="caption" color="text.secondary">
                                Prior: {pvmData.priorPeriod?.start} to {pvmData.priorPeriod?.end}
                                {' | '}
                                Current: {pvmData.currentPeriod?.start} to {pvmData.currentPeriod?.end}
                                {' | '}
                                Grouped by: {pvmData.groupBy}
                            </Typography>
                        </Box>
                    )}
                </>
            )}
        </div>
    );
};
